import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Address, ZomentumRoutes } from "@/Contracts";
import { ClientFormValues, AddClientRequest } from "@/Contracts/Clients";
import { CustomField } from "@/Contracts/Settings";
import AddTaxRegionDrawer from "@pages/TaxListPage/Drawers/TaxRegion";
import ClientsActionCreators from "@/Actions/Clients";
import UsersActionCreators from "@/Actions/User";
import useDispatch from "@/Components/Utils/useDispatch";
import { processCustomFieldFormValues } from "@/Utils";
import ZMessage from "@molecules/ZMessage";
import Form from "antd/lib/form";
import ZDrawer from "@molecules/ZDrawer";
import { IAddClientDrawerProps } from "./interface";
import { getClientFormItem } from "@templates/ZDrawers/ZClients/ZClient/getClientFormItem";
import {
	IFormSectionProps,
	IFormProps,
	EWidgets,
	ICustomFormItemProps
} from "@organisms/ZForm/interface";
import ZForm from "@organisms/ZForm";
import ZRenderCustomFieldFormItem from "@organisms/ZRenderCustomFieldFormItem";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import {
	AllRolePermissions,
	PricingPlanNames,
	TaxRegion,
	UserRole
} from "@/Contracts/Users";
import ZUserSelect from "@organisms/ZUserSelect";
import { filterOptionsOnSearch } from "@/Contracts/Utils";
import AddShippingAddressDrawer from "@pages/Drawers/Clients/ShippingAddress/index";

import {
	hasDuplicateAddressInList,
	shippingAddressAddHandler,
	shippingAddressEditHandler
} from "@/V2Utils";
import usePermissions from "@/Hooks/usePermissions";
import { Modules, ModulePermission } from "@/Contracts/Permission";
import useDatePattern from "@/Hooks/useDatePattern";
import { GlobalState } from "@/Reducers/interface";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import { FormKeys } from "@/Contracts";
import SettingsActionCreators from "@/Actions/Settings";
import useSuperOpsCrudLockout from "@/Hooks/useSuperOpsCrudLockout";
import ZAlert from "@atoms/ZAlert";

const AddClientDrawer: React.FC<IAddClientDrawerProps> = ({
	visible,
	onClose,
	onSubmit,
	shouldRedirectToClient
}) => {
	const {
		isLoading,
		isUserLoading,
		userData,
		taxRegionsList,
		addTaxRegionDrawerOpen,
		addShippingAddressDrawerOpen,
		isFormEdited,
		customFieldListClient,
		addressCustomFields
	} = useSelector((state: GlobalState) => {
		return {
			isLoading: state.clients.isLoading,
			isUserLoading: state.user.isLoading,
			userData: state.user.userData,
			taxRegionsList: state.user.taxRegionsList,
			addTaxRegionDrawerOpen: state.user.addTaxRegionDrawerOpen,
			addShippingAddressDrawerOpen:
				state.clients.shippingAddressDrawerOpen,
			isFormEdited: state.settings.isFormEdited,
			customFieldListClient:
				state.customFields.customFieldListWithoutFormulaFields
					.client_company,
			addressCustomFields: state.customFields.customFieldList.address
		};
	});

	const datePattern = useDatePattern();

	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const history = useHistory();

	const [customFieldList, setCustomFieldList] = useState<CustomField[]>([]);

	const [shippingAddresses, setShippingAddresses] = useState<Address[]>([]);
	const [currentShippingAddress, setCurrentShippingAddress] =
		useState<Address | null>(null);

	const [currentShippingAddressIndex, setCurrentShippingAddressIndex] =
		useState<number | null>(null);

	const clientAssignPermission = usePermissions(
		Modules.CLIENT_COMPANY,
		ModulePermission.EDIT_ALL,
		[AllRolePermissions.CLIENT_COMPANY_ASSIGN]
	);
	const hasClientAssignPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.SecondaryRole,
		clientAssignPermission
	);
	const isLocked = useSuperOpsCrudLockout();

	useEffect(() => {
		if (visible) {
			updateCustomFields();
		}
	}, [visible]);

	const showAddTaxRegionDrawer = () => {
		dispatch(UsersActionCreators.addTaxRegionDrawerOpened());
	};

	const closeAddTaxRegionDrawer = () => {
		dispatch(UsersActionCreators.addTaxRegionDrawerClosed());
	};

	// Helper function to extract address custom fields from form values
	const extractAddressCustomFields = (
		formValues: ClientFormValues,
		addressType: "billing" | "shipping" = "billing"
	): Record<string, any> => {
		const extractedFields: Record<string, any> = {};

		// Extract address custom fields based on address type
		Object.keys(formValues).forEach(key => {
			// For billing: "custom_fieldId"
			// For shipping: "ship_to_custom_fieldId"
			const expectedPrefix =
				addressType === "billing" ? "custom_" : "ship_to_custom_";

			if (key.startsWith(expectedPrefix) && addressCustomFields?.length) {
				// Find the corresponding custom field to get its ID
				const fieldId = key.replace(expectedPrefix, "");
				const field = addressCustomFields.find(f => f.id === fieldId);
				if (field && !field.is_system_field) {
					extractedFields[fieldId] =
						formValues[key as keyof ClientFormValues];
				}
			}
		});

		return extractedFields;
	};

	const onTaxRegionSubmit = (addTaxRegion: TaxRegion) => {
		form.setFieldsValue({
			tax_region_id: addTaxRegion.id
		});
	};

	const updateCustomFields = () => {
		setCustomFieldList(customFieldListClient);
	};

	const handleClose = () => {
		form.resetFields();
		setCustomFieldList([]);
		onClose && onClose();
		setShippingAddresses([]);
		dispatch(SettingsActionCreators.resetFormEdited(FormKeys.AddClient));
	};

	const handleCancel = () => {
		if (!isFormEdited?.[FormKeys.AddClient]) {
			handleClose();
		} else {
			ZModalConfirm({
				content:
					"Are you sure you want to discard creating the client?",
				onOk: handleClose
			});
		}
	};

	const handleAddShippingAddressDrawer = () => {
		dispatch(ClientsActionCreators.shippingAddressDrawerOpened());
	};

	const closeAddShippingAddressDrawer = () => {
		dispatch(ClientsActionCreators.shippingAddressDrawerClosed());
		setCurrentShippingAddress(null);
		setCurrentShippingAddressIndex(null);
	};

	const handleEditShippingAddress = (
		editSelectedShippingAddress: Address
	) => {
		if (currentShippingAddressIndex === null) {
			return;
		}
		const editedShippingAddress = shippingAddressEditHandler(
			editSelectedShippingAddress,
			shippingAddresses,
			currentShippingAddressIndex
		);
		setShippingAddresses(editedShippingAddress);
		ZMessage.success("Shipping Address edited successfully");
		closeAddShippingAddressDrawer();
	};

	const showEditShippingAddressDrawer = (
		$event: any,
		shippingAddress: Address,
		index: number
	) => {
		$event.domEvent.stopPropagation();
		setCurrentShippingAddress(shippingAddress);
		setCurrentShippingAddressIndex(index);
		handleAddShippingAddressDrawer();
	};

	const handleAddShippingAddress = (addSelectedShippingAddress: Address) => {
		if (
			hasDuplicateAddressInList(
				shippingAddresses,
				addSelectedShippingAddress
			)
		) {
			ZMessage.error("Shipping address already exist!");
			return;
		}
		const { updatedShippingAddresses } = shippingAddressAddHandler(
			addSelectedShippingAddress,
			shippingAddresses
		);
		setShippingAddresses(updatedShippingAddresses);
		closeAddShippingAddressDrawer();
		ZMessage.success("Shipping Address added successfully");
	};

	const addClient = async (addClientFormValues: ClientFormValues) => {
		dispatch(ClientsActionCreators.showClientsLoader());
		try {
			let addClientRequest = new AddClientRequest();

			addClientRequest.billing_address = new Address();
			addClientRequest.billing_address.address_line_1 =
				addClientFormValues.address_line_1 ?? "";
			addClientRequest.billing_address.address_line_2 =
				addClientFormValues.address_line_2 ?? "";
			addClientRequest.billing_address.city =
				addClientFormValues.city ?? "";
			addClientRequest.billing_address.country =
				addClientFormValues.country ?? "";
			addClientRequest.billing_address.pincode =
				addClientFormValues.pincode ?? "";
			addClientRequest.billing_address.state =
				addClientFormValues.state ?? "";
			addClientRequest.billing_address.phone =
				addClientFormValues.phone ?? "";

			// Note: Billing address custom fields are not supported
			// Only shipping address can have custom fields

			if (addClientFormValues.use_same_address) {
				const sameShippingAddress: Address = {
					...new Address(),
					address_line_1: addClientFormValues.address_line_1 ?? "",
					address_line_2: addClientFormValues.address_line_2 ?? "",
					pincode: addClientFormValues.pincode ?? "",
					city: addClientFormValues.city ?? "",
					country: addClientFormValues.country ?? "",
					state: addClientFormValues.state ?? "",
					phone: addClientFormValues.phone ?? "",
					is_default: !!!shippingAddresses.length
				};

				// Note: Custom fields are not copied from billing to shipping
				// Users must add them separately in shipping address

				const isBillingAddressEmpty =
					!addClientFormValues?.address_line_1?.length &&
					!addClientFormValues?.address_line_2?.length &&
					!addClientFormValues?.pincode?.length &&
					!addClientFormValues?.city?.length &&
					!addClientFormValues?.country?.length &&
					!addClientFormValues?.state?.length;

				addClientRequest.shipping_addresses = isBillingAddressEmpty
					? shippingAddresses
					: [...shippingAddresses, sameShippingAddress];
			} else {
				addClientRequest.shipping_addresses = shippingAddresses;
			}

			addClientRequest.company_type = addClientFormValues.company_type;
			addClientRequest.name = addClientFormValues.name;
			addClientRequest.owner_user_id =
				addClientFormValues.owner_user_id ?? userData?.id;
			addClientRequest.fax = addClientFormValues.fax ?? "";
			addClientRequest.website = addClientFormValues.website ?? "";
			addClientRequest.linked_in_url =
				addClientFormValues.linked_in_url ?? "";
			addClientRequest.facebook_url =
				addClientFormValues.facebook_url ?? "";
			addClientRequest.twitter_url =
				addClientFormValues.twitter_url ?? null;
			addClientRequest.market = addClientFormValues.market;
			addClientRequest.source = addClientFormValues.source ?? "";
			addClientRequest.campaign = addClientFormValues.campaign ?? "";
			addClientRequest.tax_region_id =
				addClientFormValues.tax_region_id ?? null;
			addClientRequest.mandatory_custom_field_check_require = true;
			if (addClientFormValues.date_acquired) {
				addClientRequest.date_acquired =
					addClientFormValues.date_acquired.toDate();
			}

			// Process only client company custom fields (address custom fields are handled separately)
			const customFieldFormValues =
				addClientFormValues.custom_field_form_values;

			if (customFieldFormValues && customFieldList?.length > 0) {
				addClientRequest = {
					...addClientRequest,
					custom_fields: processCustomFieldFormValues(
						customFieldFormValues,
						customFieldList
					)
				};
			}

			const addClientResponse = await dispatch(
				ClientsActionCreators.addClient(addClientRequest)
			);

			onSubmit && (await onSubmit(addClientResponse.addClient));
			handleClose();
			ZMessage.success("Client added successfully");

			if (shouldRedirectToClient) {
				dispatch(ClientsActionCreators.hideClientsLoader());
				history.push(
					ZomentumRoutes.ClientsDetail.replace(
						":id",
						addClientResponse.addClient.id
					)
				);
			}
			dispatch(ClientsActionCreators.hideClientsLoader());
		} catch (error) {
			console.error(error);
			dispatch(ClientsActionCreators.hideClientsLoader());
		}
	};

	const hasTaxEditPermission = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.UserRole,
		[AllRolePermissions.TAX_SETUP_UPDATE]
	);

	const companyTypeCustomField = customFieldList.find(
		field => field.system_field_id === "client_company_types"
	);

	const sections: IFormSectionProps[] = [
		{
			isIndentFormItems: false,
			meta: {
				formItemLayout: [5, 19],
				fields: [
					{
						key: "name",
						label: "Company",
						required: true,
						message: "Please enter company name",
						hasFeedback: true,
						widget: EWidgets.INPUT,
						widgetProps: {
							width: "full",
							placeholder: "Please enter company name"
						},
						rules: [
							{
								whitespace: true,
								message: "Company name cannot be empty"
							}
						]
					},
					...customFieldList
						?.map((customField, index) => {
							if (
								customField.system_field_id ===
								"client_owner_user_id"
							) {
								return {
									key: "owner_user_id",
									label: "Owner",
									hasFeedback: true,
									required:
										customField.mandatory_field_details
											?.for_ui,
									message: "Please select owner",
									initialValue: userData?.id ?? undefined,
									widget: ({
										value,
										onChange
									}: ICustomFormItemProps) => (
										<ZUserSelect
											showSearch
											disabled={
												!hasClientAssignPermission ||
												isLoading ||
												isUserLoading
											}
											placeholder="Please select owner"
											filterOption={filterOptionsOnSearch}
											value={value}
											onChange={v => onChange(v)}
										/>
									)
								};
							}

							const ClientFormItem = getClientFormItem({
								isDisabled: isLoading || isUserLoading,
								taxRegionsList,
								showAddTaxRegionDrawer,
								hasTaxEditPermission,
								customFieldId: customField.system_field_id,
								currentActiveClient: null,
								isBulkUpdate: false,
								handleAddShippingAddressDrawer,
								shippingAddresses,
								showEditShippingAddressDrawer,
								setShippingAddresses,
								datePattern,
								required:
									customField.mandatory_field_details?.for_ui,
								orderedAddressFields: addressCustomFields,
								companyTypeCustomField
							});

							return (
								ClientFormItem ?? {
									key: `custom_client_form_item_${index}`,
									render: () => (
										<ZRenderCustomFieldFormItem
											form={form}
											customField={customField}
											isLoading={
												isLoading || isUserLoading
											}
										/>
									)
								}
							);
						})
						.flat()
				]
			}
		}
	];

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddClient] &&
			dispatch(SettingsActionCreators.setFormEdited(FormKeys.AddClient));
	};

	const formProps: IFormProps = {
		onFinish: addClient,
		key: "AddClientForm",
		sections,
		loading: isLoading || isUserLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: handleCancel,
		isRightFooter: true,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange
	};

	return (
		<ZDrawer
			visible={visible}
			closable={true}
			destroyOnClose={true}
			onClose={handleCancel}
			title="Add Client"
			removeDefaultPadding={true}
		>
			{isLocked ? (
				<ZAlert
					type="info"
					showIcon
					message="Client management is handled by your SuperOps integration and cannot be edited here."
					className="mx-6 mt-4"
				/>
			) : (
				<ZForm {...formProps} className="py-4 overflow-y-hidden" />
			)}
			<AddTaxRegionDrawer
				visible={addTaxRegionDrawerOpen}
				onClose={closeAddTaxRegionDrawer}
				afterSubmit={onTaxRegionSubmit}
			/>
			{currentShippingAddress ? (
				<AddShippingAddressDrawer
					visible={addShippingAddressDrawerOpen}
					onClose={closeAddShippingAddressDrawer}
					shippingAddresses={shippingAddresses}
					isEditMode
					onSubmit={handleEditShippingAddress}
					currentActiveShippingAddress={currentShippingAddress}
					showDocumentShiptoAddressNote={false}
					orderedAddressFields={addressCustomFields}
				/>
			) : (
				<AddShippingAddressDrawer
					visible={addShippingAddressDrawerOpen}
					onClose={closeAddShippingAddressDrawer}
					shippingAddresses={shippingAddresses}
					onSubmit={handleAddShippingAddress}
					showDocumentShiptoAddressNote={false}
					orderedAddressFields={addressCustomFields}
				/>
			)}
		</ZDrawer>
	);
};

export default AddClientDrawer;
